import React, { PureComponent } from 'react';

type Props = {
    children: React$Node
};

const BARRIER_STYLES: Object = {
    position: 'fixed',
    top: '0px',
    left: '0px',
    width: '100vw',
    height: '100vh',
    zIndex: '999999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(9, 13, 24, 0.96)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    pointerEvents: 'all',
    userSelect: 'none'
};

/**
 * Strict security barrier rendered when meeting limit is exceeded.
 * Uses MutationObserver and periodic checks to prevent bypass via browser DevTools.
 */
export default class MeetingLimitBarrier extends PureComponent<Props> {
    _containerRef: ?HTMLDivElement;
    _mutationObserver: ?MutationObserver;
    _parentObserver: ?MutationObserver;
    _enforceInterval: ?IntervalID;

    /**
     * Initializes a new MeetingLimitBarrier instance.
     *
     * @param {Props} props - The component props.
     */
    constructor(props: Props) {
        super(props);

        this._setContainerRef = this._setContainerRef.bind(this);
        this._enforceBarrierStyles = this._enforceBarrierStyles.bind(this);
    }

    /**
     * Attaches observers and begins style enforcement.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._setupDevToolsProtection();
    }

    /**
     * Cleans up observers and intervals.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        if (this._mutationObserver) {
            this._mutationObserver.disconnect();
            this._mutationObserver = null;
        }
        if (this._parentObserver) {
            this._parentObserver.disconnect();
            this._parentObserver = null;
        }
        if (this._enforceInterval) {
            clearInterval(this._enforceInterval);
            this._enforceInterval = null;
        }
    }

    /**
     * Sets container ref and triggers protection setup.
     *
     * @param {HTMLDivElement} el - Container element.
     * @returns {void}
     */
    _setContainerRef(el: ?HTMLDivElement) {
        this._containerRef = el;
    }

    /**
     * Strictly re-applies styles if altered via DevTools.
     *
     * @returns {void}
     */
    _enforceBarrierStyles() {
        const el = this._containerRef;

        if (!el) {
            return;
        }

        for (const [ key, value ] of Object.entries(BARRIER_STYLES)) {
            if (el.style[key] !== value) {
                el.style[key] = value;
            }
        }

        if (el.hasAttribute('hidden')) {
            el.removeAttribute('hidden');
        }
    }

    /**
     * Sets up MutationObservers to guard against DevTools tampering.
     *
     * @returns {void}
     */
    _setupDevToolsProtection() {
        const el = this._containerRef;

        if (!el || typeof MutationObserver === 'undefined') {
            return;
        }

        this._enforceBarrierStyles();

        // 1. Observer for attribute/style modifications on the barrier itself
        this._mutationObserver = new MutationObserver(() => {
            this._enforceBarrierStyles();
        });

        this._mutationObserver.observe(el, {
            attributes: true,
            attributeFilter: [ 'style', 'class', 'hidden' ]
        });

        // 2. Observer on parent to prevent deletion/removal from DOM
        const parent = el.parentNode;

        if (parent) {
            this._parentObserver = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (mutation.removedNodes) {
                        for (let i = 0; i < mutation.removedNodes.length; i++) {
                            if (mutation.removedNodes[i] === el && parent.contains && !parent.contains(el)) {
                                parent.appendChild(el);
                                this._enforceBarrierStyles();
                            }
                        }
                    }
                });
            });

            this._parentObserver.observe(parent, { childList: true });
        }

        // 3. Watchdog interval checking every 1s
        this._enforceInterval = setInterval(() => {
            this._enforceBarrierStyles();
        }, 1000);
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {React$Element<any>}
     */
    render() {
        return (
            <div
                id = 'meeting-limit-barrier'
                ref = { this._setContainerRef }
                style = { BARRIER_STYLES }>
                {this.props.children}
            </div>
        );
    }
}
